import { setMonth } from 'date-fns';
import { isNumber } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import usePageMeta from 'hooks/use-page-meta';
import DataTable from 'pages/Search/components/DataTable';
import Layout from 'pages/Search/components/Layout';
import MonthFilter from 'pages/Search/components/MonthFilter';
import RedirectLink from 'pages/Search/components/RedirectLink';
import SectionHeading from 'pages/Search/components/SectionHeading';
import YearFilter from 'pages/Search/components/YearFilter';
import { ROUTES } from 'pages/Search/constants';
import { normalizeName } from 'pages/Search/utils';
import { useProductsCatalog } from 'queries/catalog/use-products-catalog';
import Utils from 'utils/Utils';

import { useYearMonthProductCatalogPayload } from './hooks/use-year-month-product-catalog-payload';
import useStyles from './styles';

const Years: React.FC = () => {
    const { classes } = useStyles();

    const layoutRef = useRef<{ scrollTop: VoidFunction } | null>(null);
    const [payload, actions] = useYearMonthProductCatalogPayload();
    const { data, isLoading } = useProductsCatalog(payload);

    useEffect(() => {
        if (data && layoutRef.current) {
            layoutRef.current.scrollTop();
        }
    }, [data]);

    const monthName = payload.month
        ? setMonth(Date.now(), payload.month - 1).toLocaleString('en-US', { month: 'long' })
        : undefined;

    const hasMetaPagination = payload.page > 1;
    const pageMeta = hasMetaPagination ? ' - Page ' + payload.page : '';

    usePageMeta({
        title:
            payload.year || payload.month
                ? `Vulnerabilities by date - ${monthName || ''} ${payload.year || ''}${pageMeta} | Vulners.com`
                : `Vulnerabilities by date${pageMeta} | Vulners.com`,
        description:
            payload.year || payload.month
                ? `Vulnerabilities released in ${monthName || ''} ${payload.year || ''}${pageMeta}`
                : `Vulnerabilities released through the years${pageMeta}`
    });

    const getPaginationHref = useCallback((page: number) => actions.url({ page }), [actions]);
    const getYearOptionHref = useCallback((year: number) => actions.url({ year }), [actions]);
    const getMonthOptionHref = useCallback((month: number) => actions.url({ month }), [actions]);

    const tableHeaders = useMemo(
        () => [
            { field: 'vendor' as const, label: 'Vendor', sort: true },
            { field: 'product' as const, label: 'Product', sort: true },
            { field: 'cves' as const, label: 'Vulnerabilities', sort: true, width: 120 }
        ],
        []
    );

    const tableData = useMemo(
        () =>
            data?.items.map(({ vendor, product, cves }, index) => [
                <RedirectLink
                    key={`vendor-${index}`}
                    to={ROUTES.VENDOR({ page: null, vendor, year: payload.year, month: payload.month })}
                    label={normalizeName(vendor)}
                    active={!!vendor}
                    className={classes.link}
                />,
                <RedirectLink
                    key={`product-${index}`}
                    to={ROUTES.PRODUCT_VERSIONS({
                        page: null,
                        vendor: vendor,
                        product: product,
                        year: payload.year,
                        month: payload.month
                    })}
                    label={normalizeName(product)}
                    active={!!vendor && !!product}
                    className={classes.link}
                />,
                <RedirectLink
                    key={`cves-${index}`}
                    to={ROUTES.PRODUCT_CVES({
                        page: null,
                        vendor: vendor,
                        product: product,
                        year: payload.year,
                        month: payload.month
                    })}
                    label={cves}
                    active={!!vendor && !!product && cves > 0}
                    className={classes.link}
                />
            ]) ?? [],
        [classes.link, data?.items, payload.month, payload.year]
    );

    const handlePageChange = useCallback((page: number) => actions.set({ page }), [actions]);
    const handlePageSizeChange = useCallback((size: number) => actions.set({ size }), [actions]);
    const handleYearSelect = useCallback((year: number) => actions.set({ year }), [actions]);
    const handleMonthSelect = useCallback((month: number) => actions.set({ month }), [actions]);

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <div className={classes.content}>
                    <SectionHeading
                        title={[
                            'Timeline',
                            payload.year?.toString(),
                            payload.month ? Utils.monthOfYear(payload.month - 1) : null
                        ]}
                        total={data?.total ?? NaN}
                        breadcrumbs={[
                            { to: ROUTES.YEARS(), label: 'Timeline' },
                            isNumber(payload.year)
                                ? {
                                      to: ROUTES.YEARS({ year: payload.year }),
                                      label: payload.year.toString()
                                  }
                                : null,
                            isNumber(payload.month)
                                ? {
                                      to: ROUTES.YEARS({ year: payload.year, month: payload.month }),
                                      label: Utils.monthOfYear(payload.month - 1)
                                  }
                                : null
                        ]}
                    />
                    <YearFilter selected={payload.year} getOptionHref={getYearOptionHref} onSelect={handleYearSelect} />
                    {payload.year ? (
                        <MonthFilter
                            selected={payload.month}
                            getOptionHref={getMonthOptionHref}
                            onSelect={handleMonthSelect}
                        />
                    ) : null}
                    <DataTable
                        headers={tableHeaders}
                        data={tableData}
                        loading={isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        getPaginationHref={getPaginationHref}
                        onSortingChange={(sort) => actions.set({ sort })}
                        page={payload.page}
                        pageSize={payload.size}
                        totalCount={data?.total}
                        sorting={payload.sort}
                    />
                </div>
            }
        />
    );
};

export default Years;
